// Interview Guide page translations
export const interviewGuideTranslations = {
  en: {
    "title": "Interview Guide",
    "subtitle": "Create Effective Interview Outlines",
    "description": "A well-structured interview outline is crucial for conducting successful research interviews. This guide helps you organize your interview into three key parts.",
    "procedures": {
      "title": "1. Interview Procedures",
      "opening": {
        "title": "Introduction & Consent",
        "items": [
          "Introduce yourself and the project",
          "Explain data collection and privacy protection",
          "Obtain clear consent for recording",
          "Outline the duration and format of the interview"
        ]
      },
      "main": {
        "title": "Main Interview",
        "items": [
          "Begin with warm-up questions",
          "Transition to the main research topics",
          "Adjust communication based on the interviewee's responses",
          "Use appropriate interview techniques for different topics"
        ]
      },
      "closing": {
        "title": "Closing",
        "items": [
          "Summarize key points",
          "Ask for final thoughts",
          "Thank the participant",
          "Explain next steps"
        ]
      }
    },
    "questions": {
      "title": "2. Interview Questions",
      "structure": "Sample Structure of Questions",
      "opening": {
        "title": "Opening Questions",
        "examples": "What brought you to this field? Could you talk about your background?"
      },
      "main": {
        "title": "Main Questions",
        "examples": "What are your thoughts on generative AI? How does it affect your work?"
      },
      "followUp": {
        "title": "Follow-up Questions",
        "examples": "Could you elaborate on that? Can you give a specific example?"
      },
      "closing": {
        "title": "Closing Questions",
        "examples": "Is there anything else you'd like to add? Any final thoughts?"
      }
    },
    "techniques": {
      "title": "3. Interview Techniques",
      "questioning": {
        "title": "Questioning Skills",
        "items": [
          "Ask open-ended questions",
          "Probe deeper",
          "Encourage detailed examples",
          "Explore emerging topics"
        ]
      },
      "communication": {
        "title": "Communication Skills",
        "items": [
          "Practice active listening",
          "Maintain a neutral stance",
          "Provide supportive feedback",
          "Use clear, academic language"
        ]
      },
      "conduct": {
        "title": "Professional Conduct",
        "items": [
          "Stay professional and objective",
          "Be culturally sensitive",
          "Respect boundaries",
          "Guide discussions naturally"
        ]
      }
    }
  },
  zh: {
    "title": "访谈指南",
    "subtitle": "创建有效的访谈大纲",
    "description": "一个结构良好的访谈大纲对于成功进行研究访谈至关重要。本指南将帮助您将访谈组织成三个关键部分。",
    "procedures": {
      "title": "1. 访谈流程",
      "opening": {
        "title": "开场与同意",
        "items": [
          "介绍自己和项目",
          "解释数据收集和隐私保护",
          "获取录制明确同意",
          "概述访谈时长和形式"
        ]
      },
      "main": {
        "title": "主要访谈",
        "items": [
          "从热身问题开始",
          "过渡到主要研究话题",
          "根据受访者的回应调整沟通方式",
          "针对不同话题运用适当的访谈技巧"
        ]
      },
      "closing": {
        "title": "结束",
        "items": [
          "总结要点",
          "询问最后想法",
          "感谢参与者",
          "解释后续步骤"
        ]
      }
    },
    "questions": {
      "title": "2. 访谈问题",
      "structure": "问题结构示例",
      "opening": {
        "title": "开场问题",
        "examples": "是什么让您进入这个领域？能否谈谈您的背景？"
      },
      "main": {
        "title": "主要问题",
        "examples": "您对生成式人工智能有什么看法？它如何影响您的工作？"
      },
      "followUp": {
        "title": "跟进问题",
        "examples": "能详细说明这一点吗？能提供一个具体的例子吗？"
      },
      "closing": {
        "title": "结束问题",
        "examples": "您还有什么要补充的吗？您最后的想法是什么？"
      }
    },
    "techniques": {
      "title": "3. 访谈技巧",
      "questioning": {
        "title": "提问技巧",
        "items": [
          "开放式提问",
          "深入追问",
          "鼓励详细举例",
          "探索新出现的主题"
        ]
      },
      "communication": {
        "title": "沟通技巧",
        "items": [
          "积极倾听",
          "保持中立立场",
          "给予支持性反馈",
          "使用清晰的学术语言"
        ]
      },
      "conduct": {
        "title": "专业行为",
        "items": [
          "保持专业客观",
          "文化敏感性",
          "尊重边界",
          "自然引导讨论"
        ]
      }
    }
  },
  fr: {
    "title": "Guide d'entretien",
    "subtitle": "Créez des plans d'entretien efficaces",
    "description": "Un plan d'entretien bien structuré est essentiel pour mener des entretiens de recherche fructueux. Ce guide vous aide à organiser votre entretien en trois parties clés.",
    "procedures": {
      "title": "1. Procédures d'entretien",
      "opening": {
        "title": "Ouverture & consentement",
        "items": [
          "Présentez-vous et présentez le projet",
          "Expliquez la collecte de données et la protection de la vie privée",
          "Obtenez un consentement clair pour l'enregistrement",
          "Indiquez la durée et le format de l'entretien"
        ]
      },
      "main": {
        "title": "Entretien principal",
        "items": [
          "Commencez par des questions d'introduction",
          "Faites la transition vers les sujets clés de la recherche",
          "Adaptez la communication en fonction des réponses de la personne interviewée",
          "Utilisez des techniques d'entretien adaptées à chaque sujet"
        ]
      },
      "closing": {
        "title": "Clôture",
        "items": [
          "Synthétisez les points clés",
          "Demandez les dernières réflexions",
          "Remerciez le participant",
          "Expliquez les prochaines étapes"
        ]
      }
    },
    "questions": {
      "title": "2. Questions d'entretien",
      "structure": "Exemple de structure de questions",
      "opening": {
        "title": "Questions d'ouverture",
        "examples": "Qu'est-ce qui vous a amené dans ce domaine ? Pourriez-vous nous parler de votre parcours ?"
      },
      "main": {
        "title": "Questions principales",
        "examples": "Que pensez-vous de l'IA générative ? Comment affecte-t-elle votre travail ?"
      },
      "followUp": {
        "title": "Questions de suivi",
        "examples": "Pourriez-vous préciser cela ? Pouvez-vous donner un exemple concret ?"
      },
      "closing": {
        "title": "Questions de clôture",
        "examples": "Y a-t-il autre chose que vous aimeriez ajouter ? Avez-vous des réflexions finales ?"
      }
    },
    "techniques": {
      "title": "3. Techniques d'entretien",
      "questioning": {
        "title": "Techniques de questionnement",
        "items": [
          "Poser des questions ouvertes",
          "Approfondir le sujet",
          "Encourager des exemples détaillés",
          "Explorer les thèmes qui émergent"
        ]
      },
      "communication": {
        "title": "Techniques de communication",
        "items": [
          "Écoute active",
          "Maintien d'une neutralité",
          "Retour d'information constructif",
          "Utilisation d'un langage académique clair"
        ]
      },
      "conduct": {
        "title": "Conduite professionnelle",
        "items": [
          "Rester professionnel et objectif",
          "Faire preuve de sensibilité culturelle",
          "Respecter les limites",
          "Guider la discussion de manière naturelle"
        ]
      }
    }
  },
  nb: {
    "title": "Intervjuguide",
    "subtitle": "Lage Effektive Intervjuoversikter",
    "description": "En velstrukturert intervjuoversikt er avgjørende for å gjennomføre vellykkede forskningsintervjuer. Denne guiden vil hjelpe deg med å organisere intervjuet ditt i tre hovedkomponenter.",
    "procedures": {
      "title": "1. Intervjuprosedyrer",
      "opening": {
        "title": "Åpning og Samtykke",
        "items": [
          "Presenter deg selv og prosjektet",
          "Forklar datainnsamling og personvern",
          "Innhent uttrykkelig samtykke til opptak",
          "Skisser intervjuets varighet og format"
        ]
      },
      "main": {
        "title": "Hovedintervju",
        "items": [
          "Start med oppvarmingsspørsmål",
          "Gå videre til hovedforskningstemaer",
          "Tilpass kommunikasjonsstilen basert på deltakerens svar",
          "Bruk passende intervjuteknikker for ulike temaer"
        ]
      },
      "closing": {
        "title": "Avslutning",
        "items": [
          "Oppsummer hovedpunkter",
          "Spør om avsluttende tanker",
          "Takk deltakeren",
          "Forklar neste steg"
        ]
      }
    },
    "questions": {
      "title": "2. Intervjuspørsmål",
      "structure": "Eksempel på Spørsmålsstruktur",
      "opening": {
        "title": "Åpningsspørsmål",
        "examples": "Hva fikk deg inn i dette feltet? Kan du fortelle om din bakgrunn?"
      },
      "main": {
        "title": "Hovedspørsmål",
        "examples": "Hva tenker du om generativ AI? Hvordan har det påvirket arbeidet ditt?"
      },
      "followUp": {
        "title": "Oppfølgingsspørsmål",
        "examples": "Kan du utdype det punktet? Kan du gi et konkret eksempel?"
      },
      "closing": {
        "title": "Avsluttende Spørsmål",
        "examples": "Er det noe mer du vil legge til? Hva er dine avsluttende tanker?"
      }
    },
    "techniques": {
      "title": "3. Intervjuteknikker",
      "questioning": {
        "title": "Spørreteknikker",
        "items": [
          "Åpne Spørsmål",
          "Oppfølgingssonderinger",
          "Oppmuntre til Detaljerte Eksempler",
          "Utforske Fremvoksende Temaer"
        ]
      },
      "communication": {
        "title": "Kommunikasjonsferdigheter",
        "items": [
          "Aktiv Lytting",
          "Nøytralt Standpunkt",
          "Støttende Tilbakemelding",
          "Tydelig Akademisk Språk"
        ]
      },
      "conduct": {
        "title": "Profesjonell Oppførsel",
        "items": [
          "Oppretthold Profesjonell Objektivitet",
          "Kulturell Sensitivitet",
          "Respektfulle Grenser",
          "Naturlig Veiledning av Diskusjonen"
        ]
      }
    }
  },
  nl: {
    "title": "Interviewgids",
    "subtitle": "Effectieve Interview Overzichten Maken",
    "description": "Een goed gestructureerd interviewoverzicht is cruciaal voor het uitvoeren van succesvolle onderzoeksinterviews. Deze gids helpt u uw interview te organiseren in drie sleuteldelen.",
    "procedures": {
      "title": "1. Interviewprocedures",
      "opening": {
        "title": "Introductie & Toestemming",
        "items": [
          "Stel uzelf en het project voor",
          "Leg gegevensverzameling en privacybescherming uit",
          "Verkrijg duidelijke toestemming voor opname",
          "Schets de duur en het formaat van het interview"
        ]
      },
      "main": {
        "title": "Hoofdinterview",
        "items": [
          "Begin met opwarmingsvragen",
          "Maak de overgang naar de belangrijkste onderzoeksonderwerpen",
          "Pas de communicatie aan op basis van de antwoorden van de geïnterviewde",
          "Gebruik geschikte interviewtechnieken voor verschillende onderwerpen"
        ]
      },
      "closing": {
        "title": "Afsluiting",
        "items": [
          "Vat de belangrijkste punten samen",
          "Vraag om laatste gedachten",
          "Bedank de deelnemer",
          "Leg de volgende stappen uit"
        ]
      }
    },
    "questions": {
      "title": "2. Interviewvragen",
      "structure": "Voorbeeldstructuur van vragen",
      "opening": {
        "title": "Openingsvragen",
        "examples": "Wat bracht u naar dit vakgebied? Kunt u iets vertellen over uw achtergrond?"
      },
      "main": {
        "title": "Hoofdvragen",
        "examples": "Wat zijn uw gedachten over generatieve AI? Hoe beïnvloedt het uw werk?"
      },
      "followUp": {
        "title": "Vervolgvragen",
        "examples": "Kunt u dat verder toelichten? Kunt u een concreet voorbeeld geven?"
      },
      "closing": {
        "title": "Afsluitende Vragen",
        "examples": "Is er nog iets dat u wilt toevoegen? Heeft u nog laatste gedachten?"
      }
    },
    "techniques": {
      "title": "3. Interviewtechnieken",
      "questioning": {
        "title": "Vraagstellingsvaardigheden",
        "items": [
          "Stel open vragen",
          "Ga dieper in",
          "Stimuleer gedetailleerde voorbeelden",
          "Verken opkomende onderwerpen"
        ]
      },
      "communication": {
        "title": "Communicatievaardigheden",
        "items": [
          "Actief luisteren",
          "Neutraal standpunt innemen",
          "Ondersteunende feedback geven",
          "Helder academisch taalgebruik"
        ]
      },
      "conduct": {
        "title": "Professioneel Gedrag",
        "items": [
          "Blijf professioneel en objectief",
          "Wees cultureel sensitief",
          "Respecteer grenzen",
          "Leid discussies op natuurlijke wijze"
        ]
      }
    }
  },
  de: {
    "title": "Interviewleitfaden",
    "subtitle": "Effektive Interviewgliederungen Erstellen",
    "description": "Ein gut strukturierter Interviewleitfaden ist entscheidend für erfolgreiche Forschungsinterviews. Dieser Leitfaden hilft Ihnen, Ihr Interview in drei wichtige Teile zu gliedern.",
    "procedures": {
      "title": "1. Interviewverfahren",
      "opening": {
        "title": "Einführung & Einwilligung",
        "items": [
          "Stellen Sie sich und das Projekt vor",
          "Erklären Sie die Datenerfassung und den Datenschutz",
          "Holen Sie eine klare Einwilligung zur Aufzeichnung ein",
          "Umreißen Sie die Dauer und das Format des Interviews"
        ]
      },
      "main": {
        "title": "Hauptinterview",
        "items": [
          "Beginnen Sie mit Aufwärmfragen",
          "Wechseln Sie zu den Hauptforschungsthemen",
          "Passen Sie die Kommunikation basierend auf den Antworten des Interviewten an",
          "Verwenden Sie geeignete Interviewtechniken für verschiedene Themen"
        ]
      },
      "closing": {
        "title": "Abschluss",
        "items": [
          "Fassen Sie die wichtigsten Punkte zusammen",
          "Fragen Sie nach abschließenden Gedanken",
          "Danken Sie dem Teilnehmer",
          "Erklären Sie die nächsten Schritte"
        ]
      }
    },
    "questions": {
      "title": "2. Interviewfragen",
      "structure": "Beispielstruktur der Fragen",
      "opening": {
        "title": "Eröffnungsfragen",
        "examples": "Was hat Sie in dieses Fachgebiet geführt? Könnten Sie über Ihren Hintergrund sprechen?"
      },
      "main": {
        "title": "Hauptfragen",
        "examples": "Was denken Sie über generative KI? Wie beeinflusst sie Ihre Arbeit?"
      },
      "followUp": {
        "title": "Folgefragen",
        "examples": "Könnten Sie das näher erläutern? Können Sie ein konkretes Beispiel nennen?"
      },
      "closing": {
        "title": "Abschlussfragen",
        "examples": "Gibt es noch etwas, das Sie hinzufügen möchten? Haben Sie abschließende Gedanken?"
      }
    },
    "techniques": {
      "title": "3. Interviewtechniken",
      "questioning": {
        "title": "Fragetechniken",
        "items": [
          "Offene Fragen stellen",
          "Tiefer nachfragen",
          "Detaillierte Beispiele fördern",
          "Aufkommende Themen erkunden"
        ]
      },
      "communication": {
        "title": "Kommunikationsfähigkeiten",
        "items": [
          "Aktives Zuhören",
          "Neutrale Haltung bewahren",
          "Unterstützendes Feedback geben",
          "Klare akademische Sprache verwenden"
        ]
      },
      "conduct": {
        "title": "Professionelles Verhalten",
        "items": [
          "Professionell und objektiv bleiben",
          "Kulturell sensibel sein",
          "Grenzen respektieren",
          "Diskussionen natürlich leiten"
        ]
      }
    }
  },
  es: {
    "title": "Guía de Entrevista",
    "subtitle": "Crear Esquemas de Entrevista Efectivos",
    "description": "Un esquema de entrevista bien estructurado es crucial para realizar entrevistas de investigación exitosas. Esta guía le ayuda a organizar su entrevista en tres partes clave.",
    "procedures": {
      "title": "1. Procedimientos de Entrevista",
      "opening": {
        "title": "Introducción y Consentimiento",
        "items": [
          "Preséntese a usted mismo y al proyecto",
          "Explique la recopilación de datos y la protección de la privacidad",
          "Obtenga consentimiento claro para la grabación",
          "Describa la duración y el formato de la entrevista"
        ]
      },
      "main": {
        "title": "Entrevista Principal",
        "items": [
          "Comience con preguntas de calentamiento",
          "Haga la transición a los temas principales de investigación",
          "Ajuste la comunicación según las respuestas del entrevistado",
          "Use técnicas de entrevista apropiadas para diferentes temas"
        ]
      },
      "closing": {
        "title": "Cierre",
        "items": [
          "Resuma los puntos clave",
          "Solicite pensamientos finales",
          "Agradezca al participante",
          "Explique los próximos pasos"
        ]
      }
    },
    "questions": {
      "title": "2. Preguntas de Entrevista",
      "structure": "Estructura de Muestra de Preguntas",
      "opening": {
        "title": "Preguntas de Apertura",
        "examples": "¿Qué le llevó a este campo? ¿Podría hablar sobre su formación?"
      },
      "main": {
        "title": "Preguntas Principales",
        "examples": "¿Qué opina sobre la IA generativa? ¿Cómo afecta su trabajo?"
      },
      "followUp": {
        "title": "Preguntas de Seguimiento",
        "examples": "¿Podría ampliar ese punto? ¿Puede dar un ejemplo concreto?"
      },
      "closing": {
        "title": "Preguntas de Cierre",
        "examples": "¿Hay algo más que desee agregar? ¿Algún pensamiento final?"
      }
    },
    "techniques": {
      "title": "3. Técnicas de Entrevista",
      "questioning": {
        "title": "Habilidades de Cuestionamiento",
        "items": [
          "Formular preguntas abiertas",
          "Profundizar más",
          "Fomentar ejemplos detallados",
          "Explorar temas emergentes"
        ]
      },
      "communication": {
        "title": "Habilidades de Comunicación",
        "items": [
          "Practicar la escucha activa",
          "Mantener una postura neutral",
          "Proporcionar retroalimentación de apoyo",
          "Usar lenguaje académico claro"
        ]
      },
      "conduct": {
        "title": "Conducta Profesional",
        "items": [
          "Mantenerse profesional y objetivo",
          "Ser culturalmente sensible",
          "Respetar los límites",
          "Guiar las discusiones de forma natural"
        ]
      }
    }
  },
  pt: {
    "title": "Guia de Entrevista",
    "subtitle": "Criar Roteiros de Entrevista Eficazes",
    "description": "Um roteiro de entrevista bem estruturado é crucial para conduzir entrevistas de pesquisa bem-sucedidas. Este guia ajuda você a organizar sua entrevista em três partes principais.",
    "procedures": {
      "title": "1. Procedimentos de Entrevista",
      "opening": {
        "title": "Introdução e Consentimento",
        "items": [
          "Apresente-se e apresente o projeto",
          "Explique a coleta de dados e a proteção de privacidade",
          "Obtenha consentimento claro para gravação",
          "Descreva a duração e o formato da entrevista"
        ]
      },
      "main": {
        "title": "Entrevista Principal",
        "items": [
          "Comece com perguntas de aquecimento",
          "Faça a transição para os principais tópicos de pesquisa",
          "Ajuste a comunicação com base nas respostas do entrevistado",
          "Use técnicas de entrevista apropriadas para diferentes tópicos"
        ]
      },
      "closing": {
        "title": "Encerramento",
        "items": [
          "Resuma os pontos principais",
          "Solicite pensamentos finais",
          "Agradeça ao participante",
          "Explique os próximos passos"
        ]
      }
    },
    "questions": {
      "title": "2. Perguntas de Entrevista",
      "structure": "Estrutura de Amostra de Perguntas",
      "opening": {
        "title": "Perguntas de Abertura",
        "examples": "O que te trouxe para esta área? Você poderia falar sobre sua formação?"
      },
      "main": {
        "title": "Perguntas Principais",
        "examples": "O que você pensa sobre IA generativa? Como ela afeta seu trabalho?"
      },
      "followUp": {
        "title": "Perguntas de Acompanhamento",
        "examples": "Poderia elaborar mais sobre isso? Pode dar um exemplo concreto?"
      },
      "closing": {
        "title": "Perguntas de Encerramento",
        "examples": "Há mais alguma coisa que gostaria de acrescentar? Algum pensamento final?"
      }
    },
    "techniques": {
      "title": "3. Técnicas de Entrevista",
      "questioning": {
        "title": "Habilidades de Questionamento",
        "items": [
          "Fazer perguntas abertas",
          "Aprofundar mais",
          "Encorajar exemplos detalhados",
          "Explorar tópicos emergentes"
        ]
      },
      "communication": {
        "title": "Habilidades de Comunicação",
        "items": [
          "Praticar escuta ativa",
          "Manter uma postura neutra",
          "Fornecer feedback de apoio",
          "Usar linguagem acadêmica clara"
        ]
      },
      "conduct": {
        "title": "Conduta Profissional",
        "items": [
          "Manter-se profissional e objetivo",
          "Ser culturalmente sensível",
          "Respeitar os limites",
          "Guiar as discussões naturalmente"
        ]
      }
    }
  },
  ja: {
    "title": "インタビューガイド",
    "subtitle": "効果的なインタビューアウトラインの作成",
    "description": "良く構造化されたインタビューアウトラインは、成功した研究インタビューを行うために不可欠です。このガイドは、インタビューを3つの重要な部分に整理するのに役立ちます。",
    "procedures": {
      "title": "1. インタビュー手順",
      "opening": {
        "title": "導入と同意",
        "items": [
          "自己紹介とプロジェクトの説明",
          "データ収集とプライバシー保護の説明",
          "録音への明確な同意を得る",
          "インタビューの時間と形式の概要説明"
        ]
      },
      "main": {
        "title": "メインインタビュー",
        "items": [
          "ウォームアップ質問から始める",
          "主要な研究トピックへ移行する",
          "インタビュー対象者の回答に基づきコミュニケーションを調整する",
          "異なるトピックに適したインタビュー技法を使用する"
        ]
      },
      "closing": {
        "title": "終了",
        "items": [
          "重要点をまとめる",
          "最後の考えを尋ねる",
          "参加者に感謝する",
          "次のステップを説明する"
        ]
      }
    },
    "questions": {
      "title": "2. インタビュー質問",
      "structure": "質問の構成例",
      "opening": {
        "title": "開始質問",
        "examples": "この分野に興味を持ったきっかけは？あなたの経歴について教えていただけますか？"
      },
      "main": {
        "title": "主要質問",
        "examples": "生成AIについてどのようにお考えですか？それはあなたの仕事にどのような影響を与えていますか？"
      },
      "followUp": {
        "title": "フォローアップ質問",
        "examples": "その点について詳しく教えていただけますか？具体的な例を挙げていただけますか？"
      },
      "closing": {
        "title": "締めくくり質問",
        "examples": "他に付け加えたいことはありますか？最後に何かお考えがありましたら？"
      }
    },
    "techniques": {
      "title": "3. インタビュー技法",
      "questioning": {
        "title": "質問スキル",
        "items": [
          "オープンエンドの質問をする",
          "深く掘り下げる",
          "詳細な例を促す",
          "新たに浮上するトピックを探る"
        ]
      },
      "communication": {
        "title": "コミュニケーションスキル",
        "items": [
          "積極的に傾聴する",
          "中立的な立場を保つ",
          "支持的なフィードバックを提供する",
          "明確な学術的言語を使用する"
        ]
      },
      "conduct": {
        "title": "プロフェッショナルな行動",
        "items": [
          "専門的かつ客観的であること",
          "文化的感受性を持つ",
          "境界を尊重する",
          "自然に議論を導く"
        ]
      }
    }
  },
  ar: {
    "title": "دليل المقابلة",
    "subtitle": "إنشاء مخططات مقابلة فعّالة",
    "description": "يعدّ مخطط المقابلة المنظم جيداً أمراً بالغ الأهمية لإجراء مقابلات بحثية ناجحة. يساعدك هذا الدليل على تنظيم مقابلتك في ثلاثة أجزاء رئيسية.",
    "procedures": {
      "title": "1. إجراءات المقابلة",
      "opening": {
        "title": "المقدمة والموافقة",
        "items": [
          "تعريف نفسك والمشروع",
          "شرح جمع البيانات وحماية الخصوصية",
          "الحصول على موافقة صريحة للتسجيل",
          "توضيح مدة وشكل المقابلة"
        ]
      },
      "main": {
        "title": "المقابلة الرئيسية",
        "items": [
          "ابدأ بأسئلة التمهيد",
          "الانتقال إلى موضوعات البحث الرئيسية",
          "تعديل التواصل بناءً على ردود المُقابَل",
          "استخدام تقنيات المقابلة المناسبة لمختلف الموضوعات"
        ]
      },
      "closing": {
        "title": "الختام",
        "items": [
          "تلخيص النقاط الرئيسية",
          "طلب الأفكار الختامية",
          "شكر المشارك",
          "شرح الخطوات التالية"
        ]
      }
    },
    "questions": {
      "title": "2. أسئلة المقابلة",
      "structure": "نموذج هيكل الأسئلة",
      "opening": {
        "title": "أسئلة الافتتاح",
        "examples": "ما الذي قادك إلى هذا المجال؟ هل يمكنك الحديث عن خلفيتك؟"
      },
      "main": {
        "title": "الأسئلة الرئيسية",
        "examples": "ما رأيك في الذكاء الاصطناعي التوليدي؟ كيف يؤثر على عملك؟"
      },
      "followUp": {
        "title": "أسئلة المتابعة",
        "examples": "هل يمكنك التوسع في ذلك؟ هل يمكنك إعطاء مثال محدد؟"
      },
      "closing": {
        "title": "أسئلة الختام",
        "examples": "هل هناك شيء آخر تود إضافته؟ ما أفكارك الختامية؟"
      }
    },
    "techniques": {
      "title": "3. تقنيات المقابلة",
      "questioning": {
        "title": "مهارات الاستجواب",
        "items": [
          "طرح أسئلة مفتوحة",
          "التعمق أكثر",
          "تشجيع الأمثلة التفصيلية",
          "استكشاف الموضوعات الناشئة"
        ]
      },
      "communication": {
        "title": "مهارات التواصل",
        "items": [
          "ممارسة الاستماع الفعّال",
          "الحفاظ على موقف محايد",
          "تقديم تغذية راجعة داعمة",
          "استخدام لغة أكاديمية واضحة"
        ]
      },
      "conduct": {
        "title": "السلوك المهني",
        "items": [
          "البقاء محترفاً وموضوعياً",
          "الحساسية الثقافية",
          "احترام الحدود",
          "توجيه النقاشات بشكل طبيعي"
        ]
      }
    }
  }
};
